import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { receivedPackage, receivedFileStreamUrl, receivedPackageUrl, claimReceived, getSession } from '../api';
import { logEvent } from '../utils/analytics';
import { ReadyToDownload } from './Recipient';
import { useStoreFreeGb, freeGbCopy } from '../hooks/useStoreFreeGb';

/**
 * /received/:recipientId#dt=…
 *
 * A package this signed-in address received, opened from the dashboard. The
 * delivery link cannot be rebuilt (its token is stored hashed), so the
 * dashboard asks the API for a 1h download token scoped to the recipient
 * row and hands it here in the URL FRAGMENT — never the query, so it stays
 * out of server logs, Referer headers and GA (the analytics sanitizer drops
 * fragments and collapses this path to /received/:id).
 *
 * The files render through the same ReadyToDownload the /r/:token page uses,
 * with the stream/zip URLs swapped for the /received/:recipientId ones. The
 * recipient-only extras that need the link token (reply thread, referral
 * card, "send files back", claim-by-token) are left out; "Save to my Store"
 * goes through claim-received, which keys on the recipient id.
 */
export default function ReceivedPackagePage() {
  const { recipientId } = useParams();
  const { hash } = useLocation();
  const dtMatch = hash.match(/[#&]dt=([^&]+)/);
  const dt = dtMatch ? decodeURIComponent(dtMatch[1]) : null;
  const [state, setState] = useState({ phase: dt ? 'loading' : 'no_token' });

  useEffect(() => {
    if (!dt) return;
    let cancelled = false;
    (async () => {
      try {
        const pkg = await receivedPackage(recipientId, dt);
        if (!cancelled) setState({ phase: 'ready', pkg });
      } catch (e) {
        if (cancelled) return;
        if (e.status === 401) setState({ phase: 'expired' });
        else if (e.status === 410) setState({ phase: 'gone' });
        else if (e.status === 404) setState({ phase: 'missing' });
        else setState({ phase: 'error', message: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [recipientId, dt]);

  if (state.phase === 'no_token' || state.phase === 'expired') {
    return (
      <div className="card">
        <h1>{state.phase === 'expired' ? 'This open link has expired' : 'Open this package from your dashboard'}</h1>
        <p className="lead">
          {state.phase === 'expired'
            ? 'A package opened from the dashboard stays open for an hour. Open it again and it will be right here.'
            : 'Received packages open from the Received list on your dashboard — nothing to type, because this is the address they were sent to.'}
        </p>
        <Link className="btn btn-primary" to="/dashboard">Go to my dashboard →</Link>
      </div>
    );
  }
  if (state.phase === 'gone' || state.phase === 'missing') {
    return (
      <div className="card">
        <h1>This package is no longer available</h1>
        <p className="lead">{state.phase === 'gone' ? 'It has expired or the sender revoked it.' : 'It is not listed under this address any more.'}</p>
        <Link className="btn btn-secondary" to="/dashboard">Back to my dashboard</Link>
      </div>
    );
  }
  if (state.phase === 'error') {
    return (
      <div className="card">
        <h1>Couldn't load this package</h1>
        <p className="error">{state.message}</p>
        <Link className="btn btn-secondary" to="/dashboard">Back to my dashboard</Link>
      </div>
    );
  }
  if (state.phase !== 'ready') return <div className="card"><p className="muted">Loading your package…</p></div>;

  const { transfer, files } = state.pkg;
  if (transfer?.e2e) return <E2EReceived transfer={transfer} files={files || []} />;

  const access = {
    stream: (fileId, opts) => receivedFileStreamUrl(recipientId, fileId, dt, opts),
    // The quick DICOM preview is an end-to-end-only artefact; a non-E2E
    // package streams the file itself. Keep the call shape so the shared
    // loader never has to ask which mode it is in.
    preview: (fileId) => receivedFileStreamUrl(recipientId, fileId, dt),
    zip: receivedPackageUrl(recipientId, dt),
  };

  return (
    <ReadyToDownload
      token={null}
      unlock={{ transfer, files: files || [], pin: null, download_token: dt }}
      access={access}
      ctas={<OwnerCtas recipientId={recipientId} transfer={transfer} />}
    />
  );
}

function OwnerCtas({ recipientId, transfer }) {
  const storeFreeGb = useStoreFreeGb();
  const [claim, setClaim] = useState(null); // { state: 'saving' | 'saved' | 'error', text }
  const me = getSession()?.user?.email;

  const save = async () => {
    setClaim({ state: 'saving' });
    try {
      const r = await claimReceived(recipientId);
      const n = r?.kept ?? 0;
      setClaim({ state: 'saved', text: n ? `Saved ${n} file${n === 1 ? '' : 's'} to your Store.` : 'Already in your Store.' });
      try { logEvent('store_claim_received', { source: 'received_page' }); } catch { /* analytics optional */ }
    } catch (e) {
      let text = e.message || 'Could not save this package.';
      if (e.status === 403 || e.code === 'NOT_RECIPIENT') text = 'This package was sent to a different email address. Sign in as that address to keep it.';
      else if (e.status === 410) text = 'This package has expired or was revoked, so there is nothing left to save.';
      else if (e.status === 404 && e.code === 'STORE_DISABLED') text = 'Store is not available yet.';
      setClaim({ state: 'error', text });
    }
  };

  return (
    <div className="recipient-ctas">
      <div className="recipient-cta" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
        <p style={{ margin: 0, fontSize: 14, color: '#134e4a' }}>
          <strong>Keep these files.</strong>{' '}
          This package expires. Save it to your Store — encrypted under your own key, filed under {transfer.sender_email || 'the sending office'}, yours to share again. {freeGbCopy(storeFreeGb, { suffix: 'free' }).replace(/^free to start$/, 'Free to start')}.
        </p>
        {claim?.state === 'saved' ? (
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#047857' }}>✓ {claim.text}</p>
        ) : (
          <button type="button" className="btn btn-primary" onClick={save} disabled={claim?.state === 'saving'}>
            {claim?.state === 'saving' ? 'Saving…' : 'Save to my Store →'}
          </button>
        )}
        {claim?.state === 'error' && <p className="error" style={{ margin: '8px 0 0' }}>{claim.text}</p>}
      </div>
      <p className="muted" style={{ marginTop: 12, fontSize: 13 }}>
        Opened as {me || 'the address this was sent to'} · <Link to="/dashboard">Back to my dashboard</Link>
      </p>
    </div>
  );
}

// An end-to-end encrypted package: the files are on the server as
// ciphertext, and the key is wrapped with the PIN the sender chose — which
// this server never had. The list is readable; the files are not, from here.
function E2EReceived({ transfer, files }) {
  return (
    <div className="card">
      <h1>{transfer.title || 'Your files'}</h1>
      <p className="lead">
        🔒 This package is end-to-end encrypted. The files can only be decrypted with the PIN the
        sender gave you, so open it from the delivery email in your inbox — the link and the PIN
        are there. Nothing here can unlock it without them.
      </p>
      <p className="muted">
        {files.length} file{files.length === 1 ? '' : 's'} · {fmtBytes(transfer.total_bytes || 0)}
        {transfer.expires_at && <> · expires {new Date(transfer.expires_at).toLocaleDateString()}</>}
        {transfer.sender_email && <> · from {transfer.sender_email}</>}
      </p>
      <ul className="files-list" style={{ marginTop: 16 }}>
        {files.map((f) => (
          <li key={f.id}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_filename}</span>
            <span className="muted">{fmtBytes(f.size_bytes)}</span>
          </li>
        ))}
      </ul>
      <p className="muted" style={{ marginTop: 14, fontSize: 13 }}>
        <Link to="/dashboard">Back to my dashboard</Link>
      </p>
    </div>
  );
}

function fmtBytes(b) {
  if (!b || b <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  return `${(b / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}
